/**
 * As fotos do veículo, uma por faixa de anos — o botão "Imagem(s)" da lista de
 * veículos do sistema antigo, que não tinha vindo para cá.
 *
 * **O arquivo não para na ApiSistema.** Ela o repassa à ApiConteudo, que grava em
 * `/dados/conteudo/veiculo` — a mesma pasta em que o `GET api/veiculosimg` procura
 * na hora da busca —, e a linha de `VeiculoImagem` guarda só o nome com a extensão.
 *
 * **A miniatura vem pela rota da busca**, e não por um atalho: se a foto aparece
 * na ficha, aparece para quem busca. Se não aparece, o arquivo não está onde a
 * busca procura, e a ficha diz isso.
 *
 * Em desenvolvimento a trava recusa as escritas: foto de veículo é dado global.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, deTeste, foraDoProjeto, obter } from "./cliente"

export type ImagemDoVeiculo = {
  id: number
  /** 0 com `anoFim` 0 = todos os anos. */
  anoIni: number
  /** 0 = sem fim: do inicial em diante. */
  anoFim: number
  /** O nome do arquivo, sem pasta — mesmo nas linhas do sistema antigo. */
  imagem: string
}

/** Os formatos que a ApiSistema aceita — os que a ApiConteudo sabe devolver. */
export const FORMATOS_DE_FOTO = "image/jpeg,image/png,image/webp"

const chave = (veiculoId: number) => ["veiculo-imagens", veiculoId] as const

/**
 * A foto é do veículo, e é o nome dele que diz à trava se a escrita é de teste:
 * em desenvolvimento só passa a de um veículo ZZTESTE. No ar não há trava, e o
 * header some antes de ir para a rede.
 */
const doVeiculo = (nomeDoVeiculo: string) => deTeste(nomeDoVeiculo, foraDoProjeto())

export function usarImagensDoVeiculo(veiculoId: number) {
  return useQuery({
    queryKey: chave(veiculoId),
    enabled: veiculoId > 0,
    queryFn: () => obter<ImagemDoVeiculo[]>(`api/veiculos/${veiculoId}/imagens`, {}),
  })
}

function formulario(anoIni: number, anoFim: number, arquivo?: File | null) {
  const corpo = new FormData()
  corpo.append("anoIni", String(anoIni))
  corpo.append("anoFim", String(anoFim))
  if (arquivo) corpo.append("arquivo", arquivo)
  return corpo
}

export function usarEnviarImagemDoVeiculo(veiculoId: number, nomeDoVeiculo: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ anoIni, anoFim, arquivo }: { anoIni: number; anoFim: number; arquivo: File }) => {
      const r = await api.post<ImagemDoVeiculo>(
        `api/veiculos/${veiculoId}/imagens`, formulario(anoIni, anoFim, arquivo), doVeiculo(nomeDoVeiculo))
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: chave(veiculoId) }),
  })
}

/** Muda a faixa e, se vier arquivo, troca a foto. */
export function usarAlterarImagemDoVeiculo(veiculoId: number, nomeDoVeiculo: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      { id, anoIni, anoFim, arquivo }: { id: number; anoIni: number; anoFim: number; arquivo?: File | null },
    ) => {
      const r = await api.put<ImagemDoVeiculo>(
        `api/veiculos/${veiculoId}/imagens/${id}`, formulario(anoIni, anoFim, arquivo), doVeiculo(nomeDoVeiculo))
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: chave(veiculoId) }),
  })
}

export function usarExcluirImagemDoVeiculo(veiculoId: number, nomeDoVeiculo: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`api/veiculos/${veiculoId}/imagens/${id}`, doVeiculo(nomeDoVeiculo))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: chave(veiculoId) }),
  })
}

/**
 * A foto como URL de blob para um `<img>`.
 *
 * O `<img src>` não manda o token, e a rota exige token — por isso a foto vem
 * pelo axios. Quem chama revoga a URL quando o componente sai.
 */
export async function urlDaImagemDoVeiculo(veiculoId: number, imagemId: number, largura?: number) {
  const r = await api.get<Blob>(`api/veiculos/${veiculoId}/imagens/${imagemId}/arquivo`, {
    params: { largura },
    responseType: "blob",
  })
  return URL.createObjectURL(r.data)
}

/** A faixa como se lê: "Todos os anos", "2015 a 2020", "2015 em diante". */
export function textoDaFaixa(anoIni: number, anoFim: number): string {
  if (anoIni === 0 && anoFim === 0) return "Todos os anos"
  if (anoFim === 0) return `${anoIni} em diante`
  if (anoIni === anoFim) return String(anoIni)
  return `${anoIni} a ${anoFim}`
}

/**
 * Por que a faixa não serve, ou `null` se serve. A mesma regra da ApiSistema,
 * aqui para avisar antes de mandar a foto.
 */
export function problemaDaFaixa(anoIni: number, anoFim: number): string | null {
  if (!Number.isInteger(anoIni) || !Number.isInteger(anoFim)) return "Ano tem de ser um número inteiro."
  if (anoIni < 0 || anoFim < 0) return "Ano não pode ser negativo. Deixe 0 para \"sem limite\"."
  for (const ano of [anoIni, anoFim]) {
    if (ano !== 0 && (ano < 1900 || ano > 2100)) return `Ano ${ano} fora do intervalo de 1900 a 2100.`
  }
  if (anoIni === 0 && anoFim > 0) {
    return "Com ano final, informe também o inicial — a busca não acha faixa que só tem fim."
  }
  if (anoFim > 0 && anoFim < anoIni) return "O ano final é menor que o inicial."
  return null
}

/** O último ano que a faixa cobre; sem fim conta como infinito. */
const fimDe = (i: ImagemDoVeiculo) => (i.anoFim === 0 ? Infinity : i.anoFim)

/**
 * As fotos que perdem para outra em algum ano. A busca pega a primeira, por ano
 * inicial e depois por ano final **como está gravado**, entre as que cobrem o ano
 * pedido — então "todos os anos" (0) ganha de qualquer faixa, e "2015 em diante"
 * (fim 0) ganha de "2015 a 2020".
 *
 * Devolve o id de cada foto encoberta com o id de quem a encobre.
 */
export function fotosEncobertas(imagens: ImagemDoVeiculo[]): Map<number, number> {
  const ordem = [...imagens].sort((a, b) => a.anoIni - b.anoIni || a.anoFim - b.anoFim || a.id - b.id)
  const encobertas = new Map<number, number>()
  ordem.forEach((depois, j) => {
    const antes = ordem.slice(0, j).find((a) => fimDe(a) >= depois.anoIni)
    if (antes) encobertas.set(depois.id, antes.id)
  })
  return encobertas
}
